import { useCallback, useId, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCustomer } from '../../lib/customer-session.js';
import { nextParam } from '../../lib/next-path.js';
import { useT } from '../../i18n/index.js';
import { Glyph } from './Glyph.js';
import { useDismiss } from './useDismiss.js';

/**
 * The account control, in the shape the staff consoles established: the person you are
 * signed in as IS the button, and signing out lives behind it.
 *
 * That placement is the point. Sign-out used to be a link at the bottom of Help and a ghost
 * button at the bottom of Profile — two places, neither of which is where anyone looks. One
 * mark in the app chrome answers both questions a passenger actually has: am I signed in,
 * and how do I stop being.
 *
 * Signed out it is not a menu at all but a single control that does the one useful thing,
 * carrying the current page in `next` so signing in returns the passenger to it.
 */

function initialsOf(name: string | undefined): string {
  const parts = (name ?? '').split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .map((part) => part[0]!)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/**
 * - `mark`    a circle in the header; the signed-out form is a labelled pill.
 * - `block`   a full-width row with name and address, for the drawer's foot.
 * - `icon`    the circle alone, for the rail, where there is no room for a word.
 */
export type AccountVariant = 'mark' | 'block' | 'icon';

/** Where the menu opens: below in a header, above in a drawer foot, beside it in a rail. */
const MENU_PLACEMENT: Record<AccountVariant, string> = {
  mark: 'nav-menu-below',
  block: 'nav-menu-up',
  icon: 'nav-menu-side',
};

export function AccountButton({ variant = 'mark' }: { variant?: AccountVariant }) {
  const t = useT();
  const location = useLocation();
  const { user, loading, signOut } = useCustomer();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);
  const menuId = useId();

  // A placeholder while the stored session is being restored. Rendering the signed-out
  // control first and swapping it a moment later tells the passenger they are logged out
  // when they are not, which is the same lie the booking screen used to tell.
  if (loading) {
    return <span className="nav-avatar nav-avatar-pending" aria-hidden />;
  }

  if (!user) {
    const to = `/auth/login?next=${nextParam(location.pathname, location.search)}`;
    if (variant === 'block') {
      return (
        <Link to={to} className="nav-account-block">
          <span className="nav-avatar nav-avatar-empty" aria-hidden>
            <Glyph name="signIn" size={17} />
          </span>
          <span className="nav-account-lines">
            <span className="nav-account-name">{t('c.signIn')}</span>
            <span className="nav-account-sub">{t('a.signInShort')}</span>
          </span>
        </Link>
      );
    }
    if (variant === 'icon') {
      return (
        <Link to={to} className="nav-account-mark" aria-label={t('c.signIn')} title={t('c.signIn')}>
          <span className="nav-avatar nav-avatar-empty" aria-hidden>
            <Glyph name="signIn" size={17} />
          </span>
        </Link>
      );
    }
    return (
      <Link to={to} className="nav-signin-pill">
        <Glyph name="signIn" size={16} />
        {t('c.signIn')}
      </Link>
    );
  }

  const avatar = user.avatarUrl ? (
    // The picture if there is one, initials otherwise. A stock silhouette would say "this
    // person has no face"; initials say "no picture yet", which is what is true.
    <img src={user.avatarUrl} alt="" className="nav-avatar nav-avatar-photo" />
  ) : (
    <span className="nav-avatar" aria-hidden>
      {initialsOf(user.name)}
    </span>
  );

  return (
    <div ref={ref} className={variant === 'block' ? 'nav-account nav-account-wide' : 'nav-account'}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        aria-label={t('a.account', { name: user.name })}
        className={variant === 'block' ? 'nav-account-block' : 'nav-account-mark'}
      >
        {avatar}
        {variant === 'block' && (
          <>
            <span className="nav-account-lines">
              <span className="nav-account-name">{user.name}</span>
              <span className="nav-account-sub">{user.email}</span>
            </span>
            <Glyph name="chevron" size={16} className={open ? 'nav-chevron-open' : 'nav-chevron'} />
          </>
        )}
      </button>

      {open && (
        <div id={menuId} role="menu" className={`nav-menu ${MENU_PLACEMENT[variant]}`}>
          <p className="nav-menu-head">
            <span className="nav-menu-name">{user.name}</span>
            <span className="nav-menu-email">{user.email}</span>
          </p>
          <Link to="/bookings" role="menuitem" onClick={close} className="nav-menu-item">
            <Glyph name="bookings" size={17} />
            {t('m.yourBookings')}
          </Link>
          <Link to="/profile" role="menuitem" onClick={close} className="nav-menu-item">
            <Glyph name="profile" size={17} />
            {t('pr.title')}
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              signOut();
            }}
            className="nav-menu-item nav-menu-leave"
          >
            <Glyph name="signOut" size={17} />
            {t('c.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}
